import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Body, Group, H7, Stack } from '@qred/components-library';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';
import { useLoanOfferSelectors } from '~/store/selectors/loanOffer.selector';
import { useCurrencyFormatter } from '~/hooks/formatter/useCurrencyFormatter';
import { LoanType } from '~/enums';
import { useGetPaymentPlanQuery } from '~/store/apis/endpoints/onboardingApi/onboarding.api';
import { getMonthlyPaymentValues } from '~/helpers/paymentPlan.helper';

const PaymentDetails = () => {
  const translate = useTranslate();
  const formatCurrency = useCurrencyFormatter();

  const {
    loanOffer: { form, overview },
  } = useSelector((state: RootState) => state);

  const { loanAmount } = useLoanOfferSelectors();

  // Debounce both loan amount and loan period to prevent excessive API calls
  const [debouncedLoanAmount, setDebouncedLoanAmount] = useState(loanAmount);
  const [debouncedLoanPeriod, setDebouncedLoanPeriod] = useState(
    form.loanPeriod
  );
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip debounce on initial mount for fast initial load
    if (isInitialMount.current) {
      isInitialMount.current = false;
      setDebouncedLoanAmount(loanAmount);
      setDebouncedLoanPeriod(form.loanPeriod);
      return;
    }

    // Wait 1 second after user stops changing the sliders before making API call
    const handler = setTimeout(() => {
      setDebouncedLoanAmount(loanAmount);
      setDebouncedLoanPeriod(form.loanPeriod);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [loanAmount, form.loanPeriod]);

  const { data: paymentPlanData, isFetching, isError } = useGetPaymentPlanQuery(
    {
      applicationUuid: overview.applicationUuid,
      loanAmount: debouncedLoanAmount,
      loanTerm: debouncedLoanPeriod,
    }
  );

  const {
    monthlyAmortization,
    monthlyFee,
    monthlyPayment,
  } = getMonthlyPaymentValues(isError ? undefined : paymentPlanData);

  return (
    <Stack spacing={'xxl'}>
      {overview.type === 'CREDIT_LIMIT_CHANGE' && (
        <Stack spacing={'sm'}>
          <Group direction="row" justify="space-between">
            <H7 data-testid="total-loan-label">{translate('TotalLoan')}</H7>
            <H7 data-testid="total-loan-value">
              {formatCurrency.format(loanAmount)}
            </H7>
          </Group>
          <Stack spacing={'xs'} data-testid="existing-loan-info-section">
            <Group direction="row" justify="space-between">
              <Body size="lg" data-testid="payout-amount-label">
                {translate('OnboardingOffer.PayoutAmount')}
              </Body>
              <Body size="lg" data-testid="payout-amount-value">
                {formatCurrency.format(form.payoutAmount)}
              </Body>
            </Group>
            <Group direction="row" justify="space-between">
              <Body size="lg" data-testid="existing-loan-label">
                {translate('ExistingLoan')}
              </Body>
              <Body size="lg" data-testid="existing-loan-value">
                {formatCurrency.format(overview.currentLoanAmountLeft ?? 0)}
              </Body>
            </Group>
          </Stack>
        </Stack>
      )}

      <Stack spacing={'sm'}>
        <Group direction="row" justify="space-between">
          <H7 data-testid="total-per-month-label">
            {overview.type === 'CREDIT_LIMIT_CHANGE'
              ? overview.loanType === LoanType.ANNUITY_LOAN
                ? translate('OnboardingOffer.YouPayQred')
                : translate('OnboardingOffer.NewTotalPerMonth')
              : translate('OnboardingOffer.TotalPerMonth')}
          </H7>
          <H7
            data-testid="total-per-month-value"
            style={{
              opacity: isFetching ? 0.5 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            {monthlyPayment !== undefined &&
              formatCurrency.format(monthlyPayment)}
          </H7>
        </Group>
        {overview.loanType === LoanType.FLEX_LOAN && (
          <Stack spacing={'xs'} data-testid="existing-loan-info-section">
            <Group direction="row" justify="space-between">
              <Body size="lg" data-testid="monthly-fee-label">
                {translate('MonthlyFee')}
              </Body>
              <Body
                size="lg"
                data-testid="monthly-fee-value"
                style={{
                  opacity: isFetching ? 0.5 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                {monthlyFee !== undefined && formatCurrency.format(monthlyFee)}
              </Body>
            </Group>
            <Group direction="row" justify="space-between">
              <Body size="lg" data-testid="monthly-amortization-label">
                {translate('MonthlyAmortization')}
              </Body>
              <Body
                size="lg"
                data-testid="monthly-amortization-value"
                style={{
                  opacity: isFetching ? 0.5 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                {monthlyAmortization !== undefined &&
                  formatCurrency.format(monthlyAmortization)}
              </Body>
            </Group>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default PaymentDetails;
